import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  styled,
} from "@mui/material";
import MoodBadIcon from "@mui/icons-material/MoodBad";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import SelfImprovementIcon from "@mui/icons-material/SelfImprovement";

const styles = {
  container: {
    fontFamily: "Poppins, sans-serif",
    py: 6,
    mt:10
  },
  title: {
    fontSize: { xs: "2rem", md: "2.5rem" },
    fontWeight: 600,
    fontFamily: "poppins",
    color: "#2C3E50",
    mb: 3,
  },
  subtitle: {
    fontSize: { xs: "1.5rem", md: "1.75rem" },
    fontWeight: 500,
    color: "#34495E",
    fontFamily: "poppins",
    mb: 2,
  },
  paragraph: {
    fontSize: "1rem",
    fontFamily: "poppins",
    color: "#5D6D7E",
    mb: 3,
    lineHeight: 1.7,
  },
  paper: {
    p: 4,
    mb: 4,
    borderRadius: 2,
    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
    transition: "transform 0.2s ease-in-out",
    "&:hover": {
      transform: "translateY(-4px)",
    },
  },
  listItem: {
    display: "flex",
    alignItems: "center",
    mb: 2,
  },
  listIcon: {
    minWidth: 40,
    color: "#3498DB",
  },
  ctaButton: {
    mt: 4,
    px: 4,
    py: 1.5,
    borderRadius: 30,
    fontSize: "1rem",
    fontWeight: 500,
    textTransform: "none",
    backgroundColor: "#2E3B55",
    boxShadow: "0 4px 14px rgba(52, 152, 219, 0.4)",
    "&:hover": {
      boxShadow: "0 6px 20px rgba(52, 152, 219, 0.6)",
      backgroundColor: "#1A2637",
    },
  },
};

const StyledListItemText = styled(ListItemText)({
  "& .MuiListItemText-primary": {
    fontWeight: 500,
    color: "#2C3E50",
    fontFamily: "poppins",
    fontSize: "1.1rem",
    marginBottom: "0.5rem",
  },
  "& .MuiListItemText-secondary": {
    color: "#5D6D7E",
    fontSize: "0.95rem",
    fontFamily: "poppins",
  },
});

const InsomniaContent = () => {
  return (
    <Container maxWidth="lg" sx={styles.container}>
      <Typography variant="h1" sx={styles.title}>
        Understanding and Overcoming Insomnia
      </Typography>

      <Paper elevation={0} sx={styles.paper}>
        <Typography variant="h2" sx={styles.subtitle}>
          What is Insomnia?
        </Typography>
        <Typography sx={styles.paragraph} textAlign={"start"}>
          Insomnia is a sleep disorder characterized by difficulty falling
          asleep or staying asleep. People with insomnia often experience
          restlessness and an inability to sleep, caused by either bodily
          discomfort or mental unrest. Even those with abundant wealth and
          luxurious lifestyles can suffer from this condition, highlighting that
          sleep is a basic need that cannot simply be bought.
        </Typography>
      </Paper>

      <Paper elevation={0} sx={styles.paper}>
        <Typography variant="h2" sx={styles.subtitle}>
          Causes of Insomnia
        </Typography>
        <List>
          <ListItem sx={styles.listItem}>
            <ListItemIcon sx={styles.listIcon}>
              <MoodBadIcon />
            </ListItemIcon>
            <StyledListItemText
              primary="Mental Causes"
              secondary="Anxiety, racing thoughts, depression, and other mental health issues can significantly impact sleep quality."
            />
          </ListItem>
          <ListItem sx={styles.listItem}>
            <ListItemIcon sx={styles.listIcon}>
              <LocalHospitalIcon />
            </ListItemIcon>
            <StyledListItemText
              primary="Physical Causes"
              secondary="Chemical imbalances in the body, physical pain, or discomfort can keep the mind aware and prevent relaxation necessary for sleep."
            />
          </ListItem>
        </List>
      </Paper>

      <Paper elevation={0} sx={styles.paper}>
        <Typography variant="h2" sx={styles.subtitle} textAlign={"center"}>
          Curing Insomnia
        </Typography>
        <Typography sx={styles.paragraph}>
          There are two primary approaches to treating insomnia:
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ bgcolor: "#F8F9FA", p: 3, borderRadius: 2 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  color: "#2C3E50",
                  fontWeight: "bold",
                  fontFamily: "poppins",
                }}
              >
                Medication
              </Typography>
              <Typography sx={styles.paragraph}>
                While effective, medication often requires lifelong use and may
                have long-term side effects. Consult a psychiatrist for proper
                guidance.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ bgcolor: "#F8F9FA", p: 3, borderRadius: 2 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  color: "#2C3E50",
                  fontWeight: "bold",
                  fontFamily: "poppins",
                }}
              >
                Meditation
              </Typography>
              <Typography sx={styles.paragraph}>
                Meditation brings calmness to the body and relaxation to the
                mind, potentially curing insomnia without side effects.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} sx={styles.paper}>
        <Typography variant="h2" sx={styles.subtitle}>
          The Power of Meditation
        </Typography>
        <Typography sx={styles.paragraph} textAlign={"start"}>
          Meditation can help create a deep space within the mind and body,
          leading to relaxation and peaceful, deep sleep. It can improve
          productivity, energy levels, and overall peace in daily life.
        </Typography>
        <Typography sx={styles.paragraph} textAlign={"start"}>
          By practicing guided meditation, you can learn to bring awareness to
          your body and mind, inducing the relaxation necessary for quality
          sleep. Our specialized techniques have shown tremendous results for
          people suffering from insomnia.
        </Typography>
      </Paper>

      <Box sx={{ textAlign: "center" }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SelfImprovementIcon />}
          sx={styles.ctaButton}
        >
          Join Our Meditation Class
        </Button>
      </Box>
    </Container>
  );
};

export default InsomniaContent;
